import React, { useContext, useState } from "react";
import { getBezierPath } from "reactflow";
import { GraphContext } from "../../../../../../contexts/GraphContext";
import { BaseContext } from "../../../../../../contexts/BaseContext";
import { createPortal } from "react-dom";
import CreateDataSlice from "../../../../../utils/CreateDataSlice";

export const TagTagEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  data,
  markerEnd,
  source,
  target,
}) => {
  const { graphViewMode, hoveredBundleId, setHoveredBundleId } =
    useContext(GraphContext);
  const { setSelectedDataSlice, totalActiveFiles } = useContext(BaseContext);
  const [isHovered, setIsHovered] = useState(false);
  const [showCreateDataSlice, setShowCreateDataSlice] = useState(false);

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Only show enhanced behavior in tag-tag mode
  if (graphViewMode === "tag-value") {
    return (
      <path
        id={id}
        style={style}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
      />
    );
  }

  const sharedValues = data?.sharedValues || 1;
  const connectingValues = (data?.connectingValues || []).filter(
    (v) => v && v.trim() !== "",
  );
  const valuesWithStats = data?.valuesWithStats || [];

  // Calculate total file count for this edge
  const totalFileCount = valuesWithStats.reduce(
    (sum, stat) => sum + (stat.fileCount || 0),
    0,
  );
  const hasFileData = totalFileCount > 0;

  const connectionLabel =
    data?.label ||
    (connectingValues.length > 0
      ? connectingValues.join(", ")
      : connectingValues.length > 1
        ? `${sharedValues} connections`
        : "");

  // Bundle information - simplified
  const isBundled = data?.isBundled || false;
  const bundleTotal = data?.bundleTotal || 1;
  const showLabel = data?.showLabel !== false;
  const isBundleEdge = data?.isBundleInput || data?.isBundleOutput || false;

  // Check if this is an edge to a bundle node
  const isEdgeToBundle =
    target && typeof target === "string" && target.startsWith("bundle_");

  // Check if this edge should be highlighted due to bundle hover
  // Only highlight if the edge has file data
  const shouldHighlightForBundle =
    hoveredBundleId &&
    hasFileData &&
    (source === hoveredBundleId || // Edge from hovered bundle (child edge)
      target === hoveredBundleId); // Edge to hovered bundle (parent edge)

  // Create conditions for data slice - build complete hierarchy path
  const createDataSliceConditions = () => {
    if (connectingValues.length > 0 && data.sourceTagName) {
      // Build the complete path including parent conditions and the current connecting values
      const conditions = [];

      // Add parent conditions from source node path (if available)
      if (data.sourceNodePath && data.sourceNodePath.length > 0) {
        // Parse parent conditions from the source node path
        for (let i = 0; i < data.sourceNodePath.length - 1; i += 2) {
          const tagName = data.sourceNodePath[i];
          const value = data.sourceNodePath[i + 1];

          if (tagName && value) {
            conditions.push({
              tag: {
                name: tagName,
                values: [value],
              },
            });
          }
        }
      }

      // Add the current connection (source tag with connecting values)
      conditions.push({
        tag: {
          name: data.sourceTagName,
          values: connectingValues,
        },
      });

      // If we have multiple conditions, use AND logic; otherwise use OR
      if (conditions.length > 1) {
        return {
          condition: "OR",
          children: [
            {
              condition: "AND",
              children: conditions,
            },
          ],
        };
      } else {
        // Single condition - just use OR structure
        return {
          condition: "OR",
          children: conditions,
        };
      }
    }
    return null;
  };

  const handleCreateDataSlice = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowCreateDataSlice(true);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    // If this edge goes to a bundle and has file data, set the bundle as hovered to trigger child edge highlighting
    if (isEdgeToBundle && hasFileData) {
      setHoveredBundleId(target);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    // Clear bundle hover when leaving any edge
    if (isEdgeToBundle) {
      setHoveredBundleId(null);
    }
  };

  return (
    <g>
      {/* Main edge path - clean styling */}
      <path
        id={id}
        style={{
          ...style,
          stroke:
            (isHovered && hasFileData) || shouldHighlightForBundle
              ? "#9CA3AF"
              : "#6B7280", // Darker gray for better visibility
          strokeWidth:
            (isHovered && hasFileData) || shouldHighlightForBundle ? 5 : 2,
          transition: "all 0.2s ease-in-out",
        }}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={{
          ...markerEnd,
          ...(((isHovered && hasFileData) || shouldHighlightForBundle) && {
            type: "arrowclosed",
            color: "#9CA3AF",
          }),
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />

      {/* Invisible wider path for easier hover detection */}
      <path
        d={edgePath}
        stroke="transparent"
        strokeWidth={30}
        fill="none"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: hasFileData ? "pointer" : "default" }}
        onClick={hasFileData ? handleCreateDataSlice : undefined}
      />

      {/* Connection label - simplified and clean */}
      {showLabel && connectionLabel && !isBundleEdge && (
        <g>
          {/* Calculate text dimensions for proper background sizing */}
          {(() => {
            // Remove percentages in parentheses from the label
            const cleanedLabel = connectionLabel
              .replace(/\s*\([^)]*\)\s*$/, "")
              .trim();
            const labelText = cleanedLabel;
            const arrowWidth = Math.max(250, labelText.length * 12 + 60);
            const arrowHeight = 80;
            const arrowHeadWidth = 30;

            // Calculate file count and percentages
            const connectionFiles = valuesWithStats.reduce(
              (sum, stat) => sum + (stat.fileCount || 0),
              0,
            );

            // Calculate percentages with correct denominators
            const totalDatasetPercentage =
              (connectionFiles / (totalActiveFiles || 1)) * 100;
            const parentNodePercentage = valuesWithStats.reduce(
              (sum, stat) => sum + (stat.parentPercentage || 0),
              0,
            );

            // Create arrow path
            const arrowPath = [
              "M",
              labelX - arrowWidth / 2,
              labelY - arrowHeight / 2,
              "L",
              labelX + arrowWidth / 2 - arrowHeadWidth,
              labelY - arrowHeight / 2,
              "L",
              labelX + arrowWidth / 2,
              labelY,
              "L",
              labelX + arrowWidth / 2 - arrowHeadWidth,
              labelY + arrowHeight / 2,
              "L",
              labelX - arrowWidth / 2,
              labelY + arrowHeight / 2,
              "Z",
            ].join(" ");

            return (
              <>
                {/* Arrow shape background */}
                <path
                  d={arrowPath}
                  fill={"rgba(255, 255, 255, 0.95)"}
                  stroke={
                    isHovered && hasFileData ? "#3B82F6" : "rgba(0, 0, 0, 0.2)"
                  }
                  strokeWidth={isHovered && hasFileData ? 2 : 1.5}
                  style={{
                    transition: "all 0.2s ease-in-out",
                    filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.15))",
                  }}
                />

                {/* Connection label text */}
                <text
                  x={labelX - 20}
                  y={labelY - 15}
                  fontSize={16}
                  fontWeight="700"
                  fontFamily="system-ui, -apple-system, sans-serif"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="pointer-events-none"
                  style={{ transition: "fill 0.2s ease-in-out" }}
                >
                  {labelText.length > 35
                    ? labelText.substring(0, 32) + "..."
                    : labelText}
                </text>

                {/* Total Dataset Percentage */}
                <g>
                  <text
                    x={labelX - 60}
                    y={labelY + 15}
                    fill={"#059669"}
                    fontSize={18}
                    fontWeight="700"
                    fontFamily="system-ui, -apple-system, sans-serif"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none"
                  >
                    {totalDatasetPercentage.toFixed(1)}%
                  </text>
                  <text
                    x={labelX - 60}
                    y={labelY + 30}
                    fill={"#6B7280"}
                    fontSize={10}
                    fontWeight="600"
                    fontFamily="system-ui, -apple-system, sans-serif"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none"
                  >
                    All Data
                  </text>
                </g>

                {/* Parent Node Percentage */}
                <g>
                  <text
                    x={labelX + 40}
                    y={labelY + 15}
                    fill={"#DC2626"}
                    fontSize={18}
                    fontWeight="700"
                    fontFamily="system-ui, -apple-system, sans-serif"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none"
                  >
                    {parentNodePercentage.toFixed(1)}%
                  </text>
                  <text
                    x={labelX + 40}
                    y={labelY + 30}
                    fill={"#6B7280"}
                    fontSize={10}
                    fontWeight="600"
                    fontFamily="system-ui, -apple-system, sans-serif"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none"
                  >
                    Parent
                  </text>
                </g>

                {/* File count (if available) */}
                {connectionFiles > 0 && (
                  <text
                    x={labelX + 90}
                    y={labelY}
                    fill={"#4B5563"}
                    fontSize={12}
                    fontWeight="600"
                    fontFamily="system-ui, -apple-system, sans-serif"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none"
                  >
                    {connectionFiles.toLocaleString()} files
                  </text>
                )}
              </>
            );
          })()}

          {/* Compact bundle indicator - no colors, just count */}
          {isBundled && bundleTotal > 1 && (
            <g>
              <circle
                cx={labelX + Math.min(connectionLabel.length * 3.5, 300) + 4}
                cy={labelY}
                r={8}
                fill="#6B7280"
              />
              <text
                x={labelX + Math.min(connectionLabel.length * 3.5, 300) + 4}
                y={labelY}
                fill="white"
                fontSize={50}
                fontWeight="bold"
                textAnchor="middle"
                dominantBaseline="middle"
                className="pointer-events-none"
              >
                {bundleTotal}
              </text>
            </g>
          )}
        </g>
      )}

      {/* Create Data Slice Modal */}
      {showCreateDataSlice &&
        createPortal(
          <CreateDataSlice
            isOpen={showCreateDataSlice}
            onClose={() => setShowCreateDataSlice(false)}
            conditions={createDataSliceConditions()}
            fileCount={totalFileCount}
            discoveryGraphData={data?.discoveryGraphData}
            selectedDiscoveryGraph={data?.selectedDiscoveryGraph}
            onSuccess={(dataSlice) => {
              setSelectedDataSlice(dataSlice);
              setShowCreateDataSlice(false);
            }}
            isGraphMode={true}
            isAllFiles={false}
          />,
          document.body,
        )}
    </g>
  );
};
